import { useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button, XIcon, Spinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { findActiveSectionId } from '@/lib/navigation-utils';
import { useNavigation } from '@/lib/navigation';
import { navigationConfig } from '@/config/navigation';
import { APP_VERSION } from '@/lib/version';
import { useBackendVersion } from '@/hooks/use-backend-version';
import { SidebarSectionV2 } from './SidebarSectionV2';

export function Sidebar() {
  const { isMobileOpen, closeMobile, expandSection, expandedSections } =
    useSidebar();
  const { sections, isLoading } = useNavigation(navigationConfig);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const { user } = useAuth();
  const isSiteAdmin = user?.role === 'SiteAdmin';
  const { version: backendVersion } = useBackendVersion(isSiteAdmin);

  // Auto-expand the section containing the active route on initial load
  // and when navigating to a new path. We intentionally exclude
  // expandedSections from deps so that manual collapse is not overridden.
  const expandedRef = useRef(expandedSections);
  expandedRef.current = expandedSections;

  useEffect(() => {
    if (sections.length === 0) return;

    const activeSectionId = findActiveSectionId(location.pathname, sections);
    if (!activeSectionId) return;

    const hasAnyExpanded = Object.values(expandedRef.current).some(Boolean);

    if (!hasAnyExpanded || location.pathname !== prevPathRef.current) {
      // No stored state or navigated to a new path: expand the active section
      expandSection(activeSectionId);
    }

    prevPathRef.current = location.pathname;
  }, [location.pathname, sections, expandSection]);

  const logoImage = (
    <img src="/favicon1.svg" alt="Skystone" className="h-8 w-8" />
  );

  return (
    <>
      {/* Backdrop for mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          // Base styles
          'fixed left-0 top-0 z-50 h-screen',
          'flex flex-col',
          'border-r border-border bg-card',
          'transition-all duration-300 ease-in-out',
          // Always expanded
          'w-64',
          // Mobile: hidden by default, slide in when open
          '-translate-x-full md:translate-x-0',
          isMobileOpen && 'translate-x-0'
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center border-b border-border px-4">
          <Link
            to="/"
            onClick={closeMobile}
            className="flex items-center gap-2"
            aria-label="Go to Dashboard"
          >
            {logoImage}
            <h1 className="text-lg font-semibold text-foreground">Skystone</h1>
          </Link>

          {/* Mobile close button */}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto md:hidden"
            onClick={closeMobile}
            aria-label="Close sidebar"
          >
            <XIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation - driven by configuration */}
        <nav className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : (
            sections.map((section) => (
              <SidebarSectionV2 key={section.id} section={section} />
            ))
          )}
        </nav>

        <div
          className="border-t border-border px-4 py-2 text-xs text-muted-foreground"
          aria-label="Application version"
        >
          <div>v{APP_VERSION}</div>
          {isSiteAdmin && backendVersion && <div>api v{backendVersion}</div>}
        </div>
      </aside>
    </>
  );
}
